import React, { useEffect, useRef, useState } from 'react';
import { FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import { useTheme } from 'styled-components';

interface CustomInputIntProps {
  name: string;
  title: string;
  value?: number | string;
  readOnly?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  minValue?: number;
  maxValue?: number;
  placeholder?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  'data-testid'?: boolean | string;
}

const CustomInputInt: React.FC<CustomInputIntProps> = ({
  name,
  title,
  value,
  readOnly = false,
  onChange,
  required = false,
  textAlign = 'right',
  minValue,
  maxValue,
  placeholder,
  onBlur,
  'data-testid': dataTestId,
}) => {
  const [internalValue, setInternalValue] = useState<string>('');
  const [cursor, setCursor] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  const controlId = name;

  const testId =
    typeof dataTestId === 'string'
      ? dataTestId
      : dataTestId === true
        ? `input-${controlId}`
        : undefined;

  const formatValue = (num: number | undefined | null): string => {
    if (num === undefined || num === null) {
      return '';
    }
    return num.toLocaleString('pt-BR');
  };

  useEffect(() => {
    let numValue: number | undefined;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed)) {
        numValue = parsed;
      }
    } else {
      numValue = value;
    }
    setInternalValue(formatValue(numValue));
  }, [value]);

  useEffect(() => {
    if (inputRef.current && cursor !== null) {
      inputRef.current.setSelectionRange(cursor, cursor);
    }
  }, [internalValue, cursor]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const inputValue = input.value;

    const selectionStart = input.selectionStart || 0;
    let digitsBeforeCursor = 0;
    for (let i = 0; i < selectionStart; i++) {
      if (/\d/.test(inputValue[i])) {
        digitsBeforeCursor++;
      }
    }

    const isNegative =
      inputValue.startsWith('-') && internalValue.length < inputValue.length;

    // Remove tudo que não for dígito
    let numericValue = inputValue.replace(/\D/g, '');

    if (numericValue === '') {
      setInternalValue('');
      if (onChange) {
        onChange({ target: { name, value: '' } } as any);
      }
      return;
    }

    if (isNegative) {
      numericValue = '-' + numericValue;
    }

    const number = parseInt(numericValue, 10);

    if (!isNaN(number)) {
      const formattedValue = number.toLocaleString('pt-BR');
      setInternalValue(formattedValue);

      let newCursor = 0;
      let digitsFound = 0;
      for (let i = 0; i < formattedValue.length; i++) {
        if (/\d/.test(formattedValue[i])) {
          digitsFound++;
        }
        if (digitsFound === digitsBeforeCursor) {
          newCursor = i + 1;
          break;
        }
      }

      if (digitsFound < digitsBeforeCursor) {
        newCursor = formattedValue.length;
      }

      setCursor(newCursor);

      if (onChange) {
        onChange({ target: { name, value: number } } as any);
      }
    }
  };

  const handleBlurInternal = (event: React.FocusEvent<HTMLInputElement>) => {
    let num = parseInt(event.target.value.replace(/\./g, ''), 10);

    if (isNaN(num)) {
      // Se o usuário apagar tudo e o componente tiver onBlur externo, chamamos assim mesmo
      if (onBlur) onBlur(event);
      return;
    }

    if (minValue !== undefined && num < minValue) num = minValue;
    if (maxValue !== undefined && num > maxValue) num = maxValue;

    const formatted = formatValue(num);
    setInternalValue(formatted);

    if (onChange) {
      onChange({ target: { name, value: num } } as any);
    }

    // Chama o onBlur externo se existir
    if (onBlur) onBlur(event);
  };

  return (
    <FormGroup controlId={name}>
      <FormLabel
        style={{ color: theme.colors.botaoConfirmar, fontWeight: 'bold' }}
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {required ? (
          <>
            <span
              style={{
                color: theme.colors.botaoConfirmar,
                fontWeight: 'bold',
              }}
            >
              {title}
            </span>
            <span style={{ color: theme.colors.vermelho, fontWeight: 'bold' }}>
              *
            </span>
          </>
        ) : (
          title
        )}
      </FormLabel>
      <FormControl
        ref={inputRef}
        type="text"
        name={name}
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlurInternal}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        style={{
          textAlign: textAlign,
          backgroundColor: readOnly ? '#e9ecef' : theme.colors.white,
        }}
      />
    </FormGroup>
  );
};

export default CustomInputInt;
